import React, { FC, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useLocation, useHistory } from 'react-router-dom';
import { buildUrlWithParameters } from '../../../utils/url-utils/urlUtils';
import { LegislationDatePicker } from './date-picker/LegislationDatePicker';
import {
  getDateKey,
  parseDateWithKeyFormat,
  getNowDate,
} from '../../../utils/date-utils/dateUtils';
import { CodeInfos } from './legislationCode.types';
import { getCodeInfos, getSectionInfos } from './legislationCode.api';
import { LegislationCodeContent } from './content/LegislationCodeContent';
import { ContentHeader } from '../../../components/content-header/ContentHeader';
import { doctrineAnalytics } from '../../../utils/analyticsUtils';
import { Hyperlink } from '../../../components/Hyperlink';
import { Breadcrumb } from '../../../components/breadcrumb/Breadcrumb';
import { MaterialIcon } from '../../../components/material-icon/MaterialIcon';
import { Row, Column } from '../../../components/grid/Grid';
import { NavigationBlock } from '../../../components/navigation-block/NavigationBlock';
import { useBreadcrumbFetcher } from '../../hooks/useBreadcrumbFetcher';

import styles from './legislation_code_main.module.scss';

type Props = {
  codeInfosInit: CodeInfos;
  dateKey: string;
  isCode: boolean;
};

export const LegislationCodeMain: FC<Props> = ({
  codeInfosInit,
  dateKey,
  isCode,
}) => {
  const intl = useIntl();
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [codeInfos, setCodeInfos] = useState<CodeInfos>(codeInfosInit);
  const [date, setDate] = useState<Date>(
    dateKey ? parseDateWithKeyFormat(dateKey).toDate() : getNowDate().toDate(),
  );

  const location = useLocation();
  const history = useHistory();
  const { legId, title, textId, siblings } = codeInfos;
  const breadcrumb = useBreadcrumbFetcher(textId, legId, date, 'section');

  const sendAnalyticsDatePicker = (dateToSend: string) => {
    if (dateToSend !== getDateKey(Date())) {
      const analyticsName = isCode
        ? 'LegislationText DatePickerDayClicked'
        : 'LegislationSection DatePickerDayClicked';
      return doctrineAnalytics(analyticsName, {
        legislationType: 'code',
        text_id: legId,
        ...(!isCode && { section_id: legId }),
        date: getDateKey(dateToSend),
      });
    }
    return null;
  };

  const changeDateVersion = async (newDate: Date | Date[]) => {
    if (newDate instanceof Date) {
      setIsLoadingCode(true);
      setDate(newDate);
      const url = buildUrlWithParameters(location.pathname, {
        dateKey: getDateKey(newDate),
      });
      history.push(url);
      sendAnalyticsDatePicker(getDateKey(newDate));
      try {
        const codeInfosUpdated = isCode
          ? await getCodeInfos(legId, newDate)
          : await getSectionInfos(textId, legId, newDate);
        setIsLoadingCode(false);
        setCodeInfos(codeInfosUpdated);
      } catch (error) {
        setIsLoadingCode(false);
        throw error;
      }
    }
  };

  return (
    <>
      {isCode ? (
        <ContentHeader
          title={title}
          topComponent={
            <Hyperlink href="/l/texts/codes" source="legislation-text">
              <FormattedMessage id="code/LegislationCode.tsx/1" />
            </Hyperlink>
          }
          subComponent={
            <LegislationDatePicker
              topParentId={textId || legId}
              isCode={isCode}
              legId={legId}
              date={date}
              onChange={changeDateVersion}
            />
          }
        />
      ) : (
        <ContentHeader
          title={title}
          topComponent={
            <Row>
              <Column medium={12} large={8}>
                <Breadcrumb breadcrumbs={breadcrumb || []} shortVersion />
              </Column>
              {siblings && (
                <Column
                  medium={12}
                  large={4}
                  className={styles.topNavigationContainer}
                >
                  {siblings.previous && (
                    <Hyperlink
                      href={siblings.previous.url}
                      source="previous_section"
                      className={styles.navigationLink}
                    >
                      <MaterialIcon
                        name="keyboard_arrow_left"
                        className={styles.navigationIcon}
                      />
                      <span className={styles.previous}>
                        <FormattedMessage id="code/LegislationCode.tsx/3" />
                      </span>
                    </Hyperlink>
                  )}
                  {siblings.next && (
                    <Hyperlink
                      href={siblings.next.url}
                      source="next_section"
                      className={styles.navigationLink}
                    >
                      <span className={styles.next}>
                        <FormattedMessage id="code/LegislationCode.tsx/4" />
                      </span>
                      <MaterialIcon
                        name="keyboard_arrow_right"
                        className={styles.navigationIcon}
                      />
                    </Hyperlink>
                  )}
                </Column>
              )}
            </Row>
          }
          subComponent={
            <LegislationDatePicker
              topParentId={textId || legId}
              isCode={isCode}
              legId={legId}
              date={date}
              onChange={changeDateVersion}
            />
          }
        />
      )}
      <LegislationCodeContent
        codeInfos={codeInfos}
        date={getDateKey(date)}
        isLoadingCode={isLoadingCode}
        isCode={isCode}
      />
      {!isCode && siblings && (
        <NavigationBlock
          blockLeft={
            siblings.previous && {
              href: siblings.previous.url,
              text: intl.formatMessage({
                id: 'code/LegislationCode.tsx/3',
              }),
              source: 'previous_section_bottom',
            }
          }
          blockRight={
            siblings.next && {
              href: siblings.next.url,
              text: intl.formatMessage({
                id: 'code/LegislationCode.tsx/4',
              }),
              source: 'next_section_bottom',
            }
          }
        />
      )}
    </>
  );
};
